import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import {
  SeatInfo,
  getSeatInfo,
  getProrationPreview,
  addSeat,
  cancelSubscription,
} from './paymentApi';
import {
  formatSeatPrice,
  formatMonthlyEquivalent,
  formatProration,
} from './priceFormatUtils';

const Subscription: React.FC = () => {
  const [seatInfo, setSeatInfo] = useState<SeatInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noSubscription, setNoSubscription] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [prorationAmount, setProrationAmount] = useState<number | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [seatQuantity, setSeatQuantity] = useState(1);

  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // @ts-ignore
  const orgId: string | undefined = (global as any)?.me?.orgID;

  const fetchSeatInfo = useCallback(async () => {
    if (!orgId) {
      setError('Organization not found');
      setLoading(false);
      return;
    }
    try {
      setError(null);
      setNoSubscription(false);
      setLoading(true);
      const res = await getSeatInfo(orgId);
      if (res.success && res.data) {
        setSeatInfo(res.data);
      } else if (res.error === 'NO_SUBSCRIPTION') {
        setNoSubscription(true);
      } else {
        setError(res.error || 'Failed to load subscription info');
      }
    } catch {
      setError('Unable to load subscription info. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchSeatInfo();
  }, [fetchSeatInfo]);

  const handleOpenDialog = async () => {
    if (!orgId) return;
    setDialogOpen(true);
    setSeatQuantity(1);
    await fetchProration(1);
  };

  const fetchProration = async (qty: number) => {
    if (!orgId) return;
    setDialogLoading(true);
    setDialogError(null);
    setProrationAmount(null);
    try {
      const res = await getProrationPreview(orgId, qty);
      if (res.success && res.data) {
        setProrationAmount(res.data.prorationAmount);
      } else {
        setDialogError(res.error || 'Failed to load proration preview');
      }
    } catch {
      setDialogError('Unable to load pricing. Please try again.');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleQuantityChange = (newQty: number) => {
    if (newQty < 1) return;
    setSeatQuantity(newQty);
    fetchProration(newQty);
  };

  const handleConfirmPurchase = async () => {
    if (!orgId) return;
    try {
      setPurchasing(true);
      setDialogError(null);
      const res = await addSeat(orgId, seatQuantity);
      if (res.success) {
        setDialogOpen(false);
        await fetchSeatInfo();
      } else {
        setDialogError(res.error || 'Failed to purchase seat');
      }
    } catch {
      setDialogError('Unable to complete purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!orgId) return;
    try {
      setCanceling(true);
      setCancelError(null);
      // Cancel at period end (not immediately) — user keeps access until trial/period ends
      const res = await cancelSubscription(orgId, false);
      if (res.success) {
        setCancelDialogOpen(false);
        await fetchSeatInfo();
      } else {
        setCancelError(res.error || 'Failed to cancel subscription');
      }
    } catch {
      setCancelError('Unable to cancel subscription. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (noSubscription) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No active subscription
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your organization does not have a seat-based subscription.
        </Typography>
      </Box>
    );
  }

  const usagePercent =
    seatInfo && seatInfo.purchasedSeats > 0
      ? (seatInfo.activeUsers / seatInfo.purchasedSeats) * 100
      : 0;

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchSeatInfo}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {seatInfo && seatInfo.customerType === 'fixed_rate' && (
        <Typography variant="body1" color="text.secondary">
          Your organization is on a fixed-rate plan with uncapped seats.
        </Typography>
      )}

      {seatInfo && seatInfo.customerType === 'seat_based' && (
        <>
          {/* Trial banner */}
          {seatInfo.subscriptionStatus === 'trialing' &&
            seatInfo.trialEndDate && (
              <Alert
                severity={seatInfo.cancelAtPeriodEnd ? 'warning' : 'info'}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {seatInfo.cancelAtPeriodEnd
                    ? 'Your trial has been canceled'
                    : "You're on a free trial"}
                </Typography>
                <Typography variant="body2">
                  {seatInfo.cancelAtPeriodEnd
                    ? `Your access will end on ${formatDate(
                        seatInfo.trialEndDate
                      )}. You will not be charged.`
                    : `Your trial ends on ${formatDate(
                        seatInfo.trialEndDate
                      )}. You will be billed ${formatSeatPrice(
                        seatInfo.pricePerSeat,
                        seatInfo.billingTerm,
                        seatInfo.currency
                      )} (${seatInfo.purchasedSeats} seat${
                        seatInfo.purchasedSeats !== 1 ? 's' : ''
                      }) on that date.`}
                </Typography>
              </Alert>
            )}

          {/* Active subscription canceled banner */}
          {seatInfo.subscriptionStatus === 'active' &&
            seatInfo.cancelAtPeriodEnd && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Your subscription has been canceled
                </Typography>
                <Typography variant="body2">
                  Your access will end on{' '}
                  <strong>
                    {seatInfo.nextBillingDate
                      ? formatDate(seatInfo.nextBillingDate)
                      : ''}
                  </strong>
                  . You will not be charged again.
                </Typography>
              </Alert>
            )}

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {seatInfo.activeUsers} / {seatInfo.purchasedSeats} seats used
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(usagePercent, 100)}
            sx={{ height: 10, borderRadius: 5, mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Minimum seats
              </Typography>
              <Typography variant="body1">{seatInfo.minimumSeats}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Available seats
              </Typography>
              <Typography variant="body1">{seatInfo.availableSeats}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Billing term
              </Typography>
              <Typography variant="body1">{seatInfo.billingTerm}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Price per seat
              </Typography>
              <Typography variant="body1">
                {formatSeatPrice(
                  seatInfo.pricePerSeat,
                  seatInfo.billingTerm,
                  seatInfo.currency
                )}
              </Typography>
              {formatMonthlyEquivalent(
                seatInfo.pricePerSeat,
                seatInfo.billingTerm,
                seatInfo.currency
              ) && (
                <Typography variant="body2" color="text.secondary">
                  {formatMonthlyEquivalent(
                    seatInfo.pricePerSeat,
                    seatInfo.billingTerm,
                    seatInfo.currency
                  )}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {seatInfo.subscriptionStatus === 'trialing'
                  ? 'Trial ends'
                  : 'Next billing date'}
              </Typography>
              <Typography variant="body1">
                {seatInfo.subscriptionStatus === 'trialing' &&
                seatInfo.trialEndDate
                  ? formatDate(seatInfo.trialEndDate)
                  : seatInfo.nextBillingDate
                  ? formatDate(seatInfo.nextBillingDate)
                  : '\u2014'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: seatInfo.cancelAtPeriodEnd
                    ? '#ff9800'
                    : seatInfo.subscriptionStatus === 'trialing'
                    ? '#2196f3'
                    : seatInfo.subscriptionStatus === 'active'
                    ? '#4caf50'
                    : '#f44336',
                  fontWeight: 600,
                }}
              >
                {seatInfo.cancelAtPeriodEnd
                  ? 'Canceling'
                  : seatInfo.subscriptionStatus === 'trialing'
                  ? 'Free Trial'
                  : seatInfo.subscriptionStatus === 'active'
                  ? 'Active'
                  : seatInfo.subscriptionStatus.charAt(0).toUpperCase() +
                    seatInfo.subscriptionStatus.slice(1)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {!seatInfo.cancelAtPeriodEnd && (
              <Button variant="contained" onClick={handleOpenDialog}>
                Purchase Additional Seats
              </Button>
            )}
            {seatInfo.selfServiceCancellation &&
              !seatInfo.cancelAtPeriodEnd && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setCancelDialogOpen(true)}
                >
                  {seatInfo.subscriptionStatus === 'trialing'
                    ? 'Cancel Trial'
                    : 'Cancel Subscription'}
                </Button>
              )}
          </Box>
          {!seatInfo.selfServiceCancellation && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              To modify or cancel your subscription, contact your account
              representative.
            </Typography>
          )}
        </>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !purchasing && setDialogOpen(false)}
      >
        <DialogTitle>Purchase Additional Seats</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
            <Typography variant="body2">Seats to add:</Typography>
            <IconButton
              size="small"
              onClick={() => handleQuantityChange(seatQuantity - 1)}
              disabled={seatQuantity <= 1 || dialogLoading || purchasing}
            >
              <Remove fontSize="small" />
            </IconButton>
            <TextField
              value={seatQuantity}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1) handleQuantityChange(val);
              }}
              size="small"
              inputProps={{ min: 1, style: { textAlign: 'center', width: 40 } }}
              disabled={dialogLoading || purchasing}
            />
            <IconButton
              size="small"
              onClick={() => handleQuantityChange(seatQuantity + 1)}
              disabled={dialogLoading || purchasing}
            >
              <Add fontSize="small" />
            </IconButton>
          </Box>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : dialogError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {dialogError}
            </Alert>
          ) : prorationAmount != null && seatInfo ? (
            <Typography sx={{ mt: 1 }}>
              Adding {seatQuantity} seat{seatQuantity > 1 ? 's' : ''} will cost
              approximately{' '}
              <strong>
                {formatProration(prorationAmount, seatInfo.currency)}
              </strong>{' '}
              for the remainder of this billing cycle.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={purchasing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmPurchase}
            disabled={dialogLoading || !!dialogError || purchasing}
          >
            {purchasing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Confirm Purchase'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel subscription dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !canceling && setCancelDialogOpen(false)}
      >
        <DialogTitle>
          {seatInfo?.subscriptionStatus === 'trialing'
            ? 'Cancel Free Trial'
            : 'Cancel Subscription'}
        </DialogTitle>
        <DialogContent>
          {seatInfo?.subscriptionStatus === 'trialing' ? (
            <Typography>
              Your free trial will remain active until{' '}
              <strong>
                {seatInfo?.trialEndDate
                  ? formatDate(seatInfo.trialEndDate)
                  : 'the end of the trial period'}
              </strong>
              . After that, your subscription will not renew and you will not be
              charged. Are you sure you want to cancel?
            </Typography>
          ) : (
            <Typography>
              Your subscription will remain active until the end of the current
              billing period (
              <strong>
                {seatInfo?.nextBillingDate
                  ? formatDate(seatInfo.nextBillingDate)
                  : ''}
              </strong>
              ). After that, it will not renew. Are you sure you want to cancel?
            </Typography>
          )}
          {cancelError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={canceling}
          >
            Keep Subscription
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelSubscription}
            disabled={canceling}
          >
            {canceling ? (
              <CircularProgress size={20} color="inherit" />
            ) : seatInfo?.subscriptionStatus === 'trialing' ? (
              'Cancel Trial'
            ) : (
              'Cancel Subscription'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subscription;
